import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import { dateToMonDDYYYY, convertTo12HourFormat } from '../utils/dateUtils';

const OpenRegistrationMatcher = ({ userData }) => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [convertingRegistration, setConvertingRegistration] = useState(null);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'date', 'name', 'match_status'
  const [overwriteMode, setOverwriteMode] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'ready_to_convert', 'already_converted', 'account_only', 'no_match'
  const [selectedRegistrations, setSelectedRegistrations] = useState(new Set());
  const [bulkActionMode, setBulkActionMode] = useState(false);
  const [bulkActionType, setBulkActionType] = useState(''); // 'convert' or 'overwrite'
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState('');

  useEffect(() => {
    fetchEvents();
    fetchRegistrations();
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      fetchRegistrations(selectedEventId);
    } else {
      fetchRegistrations();
    }
  }, [selectedEventId]);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/field-experience-events/by-stage/official', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const filteredEvents = (response.data.events || []).filter(e => e.status !== 'hidden');
      setEvents(filteredEvents.sort((a, b) => new Date(b.date) - new Date(a.date)));
    } catch (err) {
      console.error('Error fetching events:', err);
    }
  };

  const fetchRegistrations = async (eventId = null) => {
    setLoading(true);
    setError('');
    setLoadingProgress('Loading registrations...');
    try {
      const token = localStorage.getItem('token');
      const allRegistrations = [];
      const batchSize = 100; // Process in batches of 100
      const totalLimit = 500; // Total limit we want
      let offset = 0;
      let hasMore = true;
      let totalCountFromAPI = 0;

      while (hasMore && allRegistrations.length < totalLimit) {
        const params = new URLSearchParams();
        if (eventId) params.append('eventId', eventId);
        params.append('limit', Math.min(batchSize, totalLimit - allRegistrations.length).toString());
        params.append('offset', offset.toString());
        
        setLoadingProgress(`Loading batch ${Math.floor(offset / batchSize) + 1}... (${allRegistrations.length} loaded)`);
        
        const response = await axios.get(`/api/openfeeventregistrations/match-accounts?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const batchRegistrations = response.data.registrations || [];
        allRegistrations.push(...batchRegistrations);
        
        // Store total count from first batch
        if (offset === 0 && response.data.totalCount) {
          totalCountFromAPI = response.data.totalCount;
          setTotalCount(totalCountFromAPI);
        }
        
        // If we got fewer results than requested, we've reached the end
        if (batchRegistrations.length < batchSize) {
          hasMore = false;
        }
        
        offset += batchSize;
      }
      
      setRegistrations(allRegistrations);
      setLoadingProgress('');
    } catch (err) {
      setError('Failed to load registrations.');
      console.error('Error fetching registrations:', err);
      setLoadingProgress('');
    } finally {
      setLoading(false);
    }
  };

  const handleConvertRegistration = (registration) => {
    setSelectedRegistration(registration);
    setOverwriteMode(false);
    setShowConvertModal(true);
  };

  const handleOverwriteRegistration = (registration) => {
    setSelectedRegistration(registration);
    setOverwriteMode(true);
    setShowConvertModal(true);
  };

  const handleBulkAction = (actionType) => {
    setBulkActionType(actionType);
    setShowBulkConfirmModal(true);
  };

  const handleSelectRegistration = (registrationId) => {
    setSelectedRegistrations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(registrationId)) {
        newSet.delete(registrationId);
      } else {
        newSet.add(registrationId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const eligibleRegistrations = sortedRegistrations.filter(reg => {
      if (bulkActionType === 'convert') {
        return reg.matched_account && reg.enrollment_info && !(reg.existing_feevent_registration && reg.existing_feevent_registration.exists);
      } else if (bulkActionType === 'overwrite') {
        return reg.existing_feevent_registration && reg.existing_feevent_registration.exists;
      }
      return false;
    });
    
    setSelectedRegistrations(new Set(eligibleRegistrations.map(reg => reg.id)));
  };

  const handleDeselectAll = () => {
    setSelectedRegistrations(new Set());
  };

  const confirmConvertRegistration = async () => {
    if (!selectedRegistration || !selectedRegistration.matched_account || !selectedRegistration.enrollment_info) {
      setError('Cannot convert registration without a matched account and enrollment.');
      return;
    }

    setConvertingRegistration(selectedRegistration.id);
    try {
      const token = localStorage.getItem('token');
      const endpoint = overwriteMode 
        ? `/api/openfeeventregistrations/${selectedRegistration.id}/overwrite-feeventregistration`
        : `/api/openfeeventregistrations/${selectedRegistration.id}/convert-to-feeventregistration`;
      
      const response = await axios.post(
        endpoint,
        {
          enrollment_id: selectedRegistration.enrollment_info.enrollment_id,
          account_id: selectedRegistration.matched_account.account_id,
          overwrite: overwriteMode
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const successMessage = overwriteMode 
        ? `Successfully overwrote existing fee event registration! Open ID: ${selectedRegistration.id}, Fee Event Registration ID: ${response.data.registration.id}`
        : `Successfully converted registration! Open ID: ${selectedRegistration.id}, Fee Event Registration ID: ${response.data.registration.id}`;
      
      setSuccess(successMessage);
      setShowConvertModal(false);
      setSelectedRegistration(null);
      setOverwriteMode(false);
      
      // Refresh the registrations list
      await fetchRegistrations(selectedEventId);
    } catch (err) {
      if (err.response?.status === 409) {
        setError('A fee event registration already exists for this student and event.');
      } else {
        setError(err.response?.data?.error || 'Failed to convert registration.');
      }
    } finally {
      setConvertingRegistration(null);
    }
  };

  const confirmBulkAction = async () => {
    if (selectedRegistrations.size === 0) {
      setError('No registrations selected for bulk action.');
      return;
    }

    setBulkProcessing(true);
    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    try {
      const token = localStorage.getItem('token');
      const selectedRegs = sortedRegistrations.filter(reg => selectedRegistrations.has(reg.id));
      
      console.log('Starting bulk action:', {
        actionType: bulkActionType,
        selectedCount: selectedRegs.length,
        selectedRegs: selectedRegs.map(r => ({ id: r.id, name: r.name }))
      });
      
      for (const registration of selectedRegs) {
        try {
          console.log('Processing registration:', registration.id, registration.name);
          
          const endpoint = bulkActionType === 'overwrite' 
            ? `/api/openfeeventregistrations/${registration.id}/overwrite-feeventregistration`
            : `/api/openfeeventregistrations/${registration.id}/convert-to-feeventregistration`;
          
          const requestData = {
            enrollment_id: registration.enrollment_info.enrollment_id,
            account_id: registration.matched_account.account_id,
            overwrite: bulkActionType === 'overwrite'
          };
          
          console.log('API request:', { endpoint, requestData });
          
          await axios.post(
            endpoint,
            requestData,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          
          console.log('Successfully processed:', registration.id);
          successCount++;
        } catch (err) {
          console.error('Error processing registration:', registration.id, registration.name, err);
          console.error('Registration details:', {
            id: registration.id,
            name: registration.name,
            email: registration.email,
            feevent: registration.feevent,
            enrollment_id: registration.enrollment_info?.enrollment_id,
            account_id: registration.matched_account?.account_id,
            error: err.response?.data || err.message
          });
          errorCount++;
          errors.push(`${registration.name}: ${err.response?.data?.error || err.message || 'Unknown error'}`);
        }
      }

      if (successCount > 0) {
        setSuccess(`Bulk ${bulkActionType} completed: ${successCount} successful, ${errorCount} failed.`);
      }
      
      if (errorCount > 0) {
        setError(`Some operations failed: ${errors.slice(0, 3).join('; ')}${errors.length > 3 ? '...' : ''}`);
      }

      setShowBulkConfirmModal(false);
      setSelectedRegistrations(new Set());
      setBulkActionType('');
      
      // Refresh the registrations list
      await fetchRegistrations(selectedEventId);
    } catch (err) {
      console.error('Bulk operation failed:', err);
      setError('Bulk operation failed: ' + (err.response?.data?.error || err.message || 'Unknown error'));
    } finally {
      setBulkProcessing(false);
    }
  };

  const getMatchStatus = (registration) => {
    // Check if there's already a fee event registration
    if (registration.existing_feevent_registration && registration.existing_feevent_registration.exists) {
      return { 
        text: '🔄 Already Converted', 
        color: '#6c757d',
        description: `Fee event registration exists (${registration.existing_feevent_registration.status})`
      };
    }
    
    if (registration.matched_account && registration.enrollment_info) {
      return { 
        text: '✅ Full Match', 
        color: '#28a745',
        description: `Matched by ${registration.matched_account.match_type}`
      };
    } else if (registration.matched_account) {
      return { 
        text: '⚠️ Account Only', 
        color: '#ffc107',
        description: 'Account found but no active enrollment'
      };
    } else {
      return { 
        text: '❌ No Match', 
        color: '#dc3545',
        description: 'No matching account found'
      };
    }
  };

  const getAttendanceStatus = (registration) => {
    if (registration.present === false) {
      return { text: '❌ Absent', color: '#dc3545' };
    } else if (registration.present === true) {
      return { 
        text: `✅ Present (${registration.attendancetype || 'Unknown'})`, 
        color: '#28a745' 
      };
    } else {
      return { text: 'Not Marked', color: '#6c757d' };
    }
  };

  const filteredRegistrations = registrations.filter(reg => {
    // Apply search filter
    const matchesSearch = !searchTerm || (() => {
      const searchLower = searchTerm.toLowerCase();
      return (
        reg.name.toLowerCase().includes(searchLower) ||
        (reg.email && reg.email.toLowerCase().includes(searchLower)) ||
        (reg.event_title && reg.event_title.toLowerCase().includes(searchLower))
      );
    })();
    
    // Apply status filter
    const matchesStatus = (() => {
      switch (statusFilter) {
        case 'ready_to_convert':
          return reg.matched_account && reg.enrollment_info && !(reg.existing_feevent_registration && reg.existing_feevent_registration.exists);
        case 'already_converted':
          return reg.existing_feevent_registration && reg.existing_feevent_registration.exists;
        case 'account_only':
          return reg.matched_account && !reg.enrollment_info && !(reg.existing_feevent_registration && reg.existing_feevent_registration.exists);
        case 'no_match':
          return !reg.matched_account;
        case 'all':
        default:
          return true;
      }
    })();
    
    return matchesSearch && matchesStatus;
  });

  const sortedRegistrations = [...filteredRegistrations].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'match_status':
        const aMatch = getMatchStatus(a);
        const bMatch = getMatchStatus(b);
        if (aMatch.text === bMatch.text) return a.name.localeCompare(b.name);
        return aMatch.text.localeCompare(bMatch.text);
      case 'date':
      default:
        return new Date(b.createddate) - new Date(a.createddate);
    }
  });

  if (loading) {
    return (
      <div style={styles.container}>
        <LoadingSpinner />
        {loadingProgress && (
          <div style={styles.loadingProgress}>
            {loadingProgress}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Open Registration Matcher</h1>
      <p style={styles.subtitle}>
        Match open fee event registrations with student accounts and convert them to fee event registrations.
      </p>
      
      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}
      
      <div style={styles.controls}>
        <div style={styles.controlGroup}>
          <label style={styles.label}>Filter by Event:</label>
          <select 
            value={selectedEventId} 
            onChange={(e) => setSelectedEventId(e.target.value)}
            style={styles.select}
          >
            <option value="">All Events</option>
            {events.map(event => (
              <option key={event.id} value={event.id}>
                {event.date ? dateToMonDDYYYY(event.date, false) : 'No Date'} - {event.title}
              </option>
            ))}
          </select>
        </div>
        
        <div style={styles.controlGroup}>
          <label style={styles.label}>Search:</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, email, or event..."
            style={styles.searchInput}
          />
        </div>
        
        <div style={styles.controlGroup}>
          <label style={styles.label}>Filter by Status:</label>
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            style={styles.select}
          >
            <option value="all">All Registrations</option>
            <option value="ready_to_convert">Ready to Convert</option>
            <option value="already_converted">Already Converted</option>
            <option value="account_only">Account Only</option>
            <option value="no_match">No Match</option>
          </select>
        </div>
        
        <div style={styles.controlGroup}>
          <label style={styles.label}>Sort by:</label>
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            style={styles.select}
          >
            <option value="date">Date (Newest First)</option>
            <option value="name">Name</option>
            <option value="match_status">Match Status</option>
          </select>
        </div>
      </div>

      <div style={styles.stats}>
        <div style={styles.statItem}>
          <strong>Showing:</strong> {sortedRegistrations.length} of {registrations.length} registrations
          {totalCount > registrations.length && (
            <span style={styles.totalCountIndicator}>
              {' '}({totalCount} total available)
            </span>
          )}
          {statusFilter !== 'all' && (
            <span style={styles.filterIndicator}>
              {' '}(Filtered by: {statusFilter.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())})
            </span>
          )}
        </div>
        <div style={styles.statItem}>
          <strong>Already Converted:</strong> {registrations.filter(r => r.existing_feevent_registration && r.existing_feevent_registration.exists).length}
        </div>
        <div style={styles.statItem}>
          <strong>Ready to Convert:</strong> {registrations.filter(r => r.matched_account && r.enrollment_info && !(r.existing_feevent_registration && r.existing_feevent_registration.exists)).length}
        </div>
        <div style={styles.statItem}>
          <strong>Account Only:</strong> {registrations.filter(r => r.matched_account && !r.enrollment_info && !(r.existing_feevent_registration && r.existing_feevent_registration.exists)).length}
        </div>
        <div style={styles.statItem}>
          <strong>No Match:</strong> {registrations.filter(r => !r.matched_account).length}
        </div>
      </div>

      {/* Bulk Actions */}
      <div style={styles.bulkActions}>
        <div style={styles.bulkActionControls}>
          <button
            style={styles.bulkActionButton}
            onClick={() => {
              setBulkActionType('convert');
              setBulkActionMode(true);
            }}
            disabled={bulkProcessing}
          >
            Bulk Convert Ready
          </button>
          <button
            style={styles.bulkActionButton}
            onClick={() => {
              setBulkActionType('overwrite');
              setBulkActionMode(true);
            }}
            disabled={bulkProcessing}
          >
            Bulk Overwrite Converted
          </button>
          {bulkActionMode && (
            <div style={styles.bulkSelectionControls}>
              <button
                style={styles.bulkSelectButton}
                onClick={handleSelectAll}
                disabled={bulkProcessing}
              >
                Select All Eligible
              </button>
              <button
                style={styles.bulkSelectButton}
                onClick={handleDeselectAll}
                disabled={bulkProcessing}
              >
                Deselect All
              </button>
              <span style={styles.bulkSelectionCount}>
                {selectedRegistrations.size} selected
              </span>
              <button
                style={styles.bulkConfirmButton}
                onClick={() => handleBulkAction(bulkActionType)}
                disabled={selectedRegistrations.size === 0 || bulkProcessing}
              >
                {bulkActionType === 'convert' ? 'Convert Selected' : 'Overwrite Selected'}
              </button>
              <button
                style={styles.bulkCancelButton}
                onClick={() => {
                  setBulkActionMode(false);
                  setBulkActionType('');
                  setSelectedRegistrations(new Set());
                }}
                disabled={bulkProcessing}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={styles.registrationsList}>
        {sortedRegistrations.length === 0 ? (
          <div style={styles.noRegistrations}>
            {searchTerm || statusFilter !== 'all' 
              ? `No registrations match your ${searchTerm ? 'search and ' : ''}filter criteria.` 
              : 'No registrations found.'}
          </div>
        ) : (
          sortedRegistrations.map((registration) => {
            const matchStatus = getMatchStatus(registration);
            const attendanceStatus = getAttendanceStatus(registration);
            const isConverting = convertingRegistration === registration.id;

            return (
              <div key={registration.id} style={styles.registrationCard}>
                <div style={styles.registrationHeader}>
                  <div style={styles.studentInfo}>
                    {bulkActionMode && (
                      <input
                        type="checkbox"
                        checked={selectedRegistrations.has(registration.id)}
                        onChange={() => handleSelectRegistration(registration.id)}
                        disabled={bulkProcessing || (
                          bulkActionType === 'convert' && !(registration.matched_account && registration.enrollment_info && !(registration.existing_feevent_registration && registration.existing_feevent_registration.exists))
                        ) || (
                          bulkActionType === 'overwrite' && !(registration.existing_feevent_registration && registration.existing_feevent_registration.exists)
                        )}
                        style={styles.checkbox}
                      />
                    )}
                    <h3 style={styles.studentName}>{registration.name}</h3>
                    <div style={styles.studentDetails}>
                      <span><strong>Open Registration ID:</strong> {registration.id}</span>
                      <span><strong>Grade:</strong> {registration.grade}</span>
                      {registration.email && (
                        <span><strong>Email:</strong> {registration.email}</span>
                      )}
                    </div>
                  </div>
                  <div style={styles.statusBadges}>
                    <span style={{
                      ...styles.statusBadge,
                      backgroundColor: matchStatus.color + '20',
                      color: matchStatus.color
                    }}>
                      {matchStatus.text}
                    </span>
                    <span style={{
                      ...styles.statusBadge,
                      backgroundColor: attendanceStatus.color + '20',
                      color: attendanceStatus.color
                    }}>
                      {attendanceStatus.text}
                    </span>
                  </div>
                </div>

                <div style={styles.eventInfo}>
                  <h4 style={styles.eventTitle}>{registration.event_title}</h4>
                  <div style={styles.eventDetails}>
                    <span><strong>Date:</strong> {registration.event_date ? dateToMonDDYYYY(registration.event_date, false) : 'TBA'}</span>
                    <span><strong>Time:</strong> {convertTo12HourFormat(registration.event_start_time) || 'TBA'} - {convertTo12HourFormat(registration.event_end_time) || 'TBA'}</span>
                    <span><strong>Location:</strong> {registration.event_location || 'TBA'}</span>
                    <span><strong>Hours:</strong> {registration.hours || 'N/A'}</span>
                  </div>
                </div>

                {registration.existing_feevent_registration && registration.existing_feevent_registration.exists ? (
                  <div style={styles.existingRegistrationInfo}>
                    <h4 style={styles.existingRegistrationTitle}>Existing Fee Event Registration:</h4>
                    <div style={styles.existingRegistrationDetails}>
                      <span><strong>Fee Event Registration ID:</strong> {registration.existing_feevent_registration.registration_id}</span>
                      <span><strong>Status:</strong> {registration.existing_feevent_registration.status}</span>
                      <span><strong>Attended:</strong> {registration.existing_feevent_registration.attended ? 'Yes' : 'No'}</span>
                      <span><strong>Hours:</strong> {registration.existing_feevent_registration.hours || 'N/A'}</span>
                      <span><strong>Created:</strong> {new Date(registration.existing_feevent_registration.createddate).toLocaleDateString()}</span>
                    </div>
                    <div style={styles.existingRegistrationNote}>
                      ℹ️ This open registration has already been converted to a fee event registration
                    </div>
                  </div>
                ) : registration.matched_account && (
                  <div style={styles.matchInfo}>
                    <h4 style={styles.matchTitle}>Matched Account:</h4>
                    <div style={styles.accountInfo}>
                      <span><strong>Name:</strong> {registration.matched_account.first_name} {registration.matched_account.last_name}</span>
                      <span><strong>Email:</strong> {registration.matched_account.primary_email}</span>
                      <span><strong>Match Type:</strong> {registration.matched_account.match_type}</span>
                    </div>
                    
                    {registration.enrollment_info ? (
                      <div style={styles.enrollmentInfo}>
                        <h5 style={styles.enrollmentTitle}>Enrollment Info:</h5>
                        <div style={styles.enrollmentDetails}>
                          <span><strong>Grade Level:</strong> {registration.enrollment_info.gradelevel}</span>
                          <span><strong>Enrollment ID:</strong> {registration.enrollment_info.enrollment_id}</span>
                          <span><strong>Status:</strong> Active</span>
                        </div>
                      </div>
                    ) : (
                      <div style={styles.noEnrollmentWarning}>
                        ⚠️ No active enrollment found for this account
                      </div>
                    )}
                  </div>
                )}

                {!registration.matched_account && (
                  <div style={styles.noMatchInfo}>
                    <h4 style={styles.noMatchTitle}>No Account Match Found</h4>
                    <p style={styles.noMatchDescription}>
                      Could not find a matching account by name or email. You may need to manually create an account or enrollment.
                    </p>
                  </div>
                )}

                <div style={styles.actions}>
                  {!bulkActionMode ? (
                    <>
                      {registration.existing_feevent_registration && registration.existing_feevent_registration.exists ? (
                        <div style={styles.actionGroup}>
                          <button
                            style={styles.overwriteButton}
                            onClick={() => handleOverwriteRegistration(registration)}
                            disabled={isConverting}
                          >
                            {isConverting ? 'Overwriting...' : 'Overwrite Existing'}
                          </button>
                          <button
                            style={styles.disabledButton}
                            disabled
                          >
                            Already Converted
                          </button>
                        </div>
                      ) : registration.matched_account && registration.enrollment_info ? (
                        <button
                          style={styles.convertButton}
                          onClick={() => handleConvertRegistration(registration)}
                          disabled={isConverting}
                        >
                          {isConverting ? 'Converting...' : 'Convert to Fee Event Registration'}
                        </button>
                      ) : (
                        <button
                          style={styles.disabledButton}
                          disabled
                        >
                          Cannot Convert - Missing Match
                        </button>
                      )}
                    </>
                  ) : (
                    <div style={styles.bulkModeIndicator}>
                      {selectedRegistrations.has(registration.id) ? (
                        <span style={styles.selectedIndicator}>✓ Selected</span>
                      ) : (
                        <span style={styles.unselectedIndicator}>Not Selected</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Convert Modal */}
      {showConvertModal && selectedRegistration && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Confirm Conversion</h3>
              <button
                style={styles.modalCloseButton}
                onClick={() => {
                  setShowConvertModal(false);
                  setSelectedRegistration(null);
                }}
              >
                ×
              </button>
            </div>
            <div style={styles.modalBody}>
              <p style={styles.modalText}>
                {overwriteMode 
                  ? 'Are you sure you want to overwrite the existing fee event registration with data from this open registration?'
                  : 'Are you sure you want to convert this open registration to a fee event registration?'
                }
              </p>
              <div style={styles.modalDetails}>
                <div><strong>Open Registration ID:</strong> {selectedRegistration.id}</div>
                <div><strong>Student:</strong> {selectedRegistration.name}</div>
                <div><strong>Event:</strong> {selectedRegistration.event_title}</div>
                <div><strong>Matched Account:</strong> {selectedRegistration.matched_account.first_name} {selectedRegistration.matched_account.last_name}</div>
                <div><strong>Enrollment:</strong> {selectedRegistration.enrollment_info.enrollment_id}</div>
                {overwriteMode && selectedRegistration.existing_feevent_registration && (
                  <div style={styles.overwriteWarning}>
                    <strong>⚠️ Warning:</strong> This will replace the existing fee event registration (ID: {selectedRegistration.existing_feevent_registration.registration_id}) with the following data:
                    <ul style={styles.overwriteList}>
                      <li><strong>Hours:</strong> {selectedRegistration.hours || 'N/A'} (was {selectedRegistration.existing_feevent_registration.hours || 'N/A'})</li>
                      <li><strong>Attendance:</strong> {selectedRegistration.present ? 'Present' : 'Absent'} ({selectedRegistration.attendancetype || 'Unknown'})</li>
                      <li><strong>Status:</strong> Will be reset to 'Pending'</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <div style={styles.modalActions}>
              <button
                style={styles.modalCancelButton}
                onClick={() => {
                  setShowConvertModal(false);
                  setSelectedRegistration(null);
                }}
              >
                Cancel
              </button>
              <button
                style={styles.modalConfirmButton}
                onClick={confirmConvertRegistration}
                disabled={convertingRegistration}
              >
                {convertingRegistration ? (overwriteMode ? 'Overwriting...' : 'Converting...') : (overwriteMode ? 'Overwrite' : 'Convert')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Confirmation Modal */}
      {showBulkConfirmModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                Confirm Bulk {bulkActionType === 'convert' ? 'Conversion' : 'Overwrite'}
              </h3>
              <button
                style={styles.modalCloseButton}
                onClick={() => {
                  setShowBulkConfirmModal(false);
                  setSelectedRegistrations(new Set());
                  setBulkActionType('');
                }}
              >
                ×
              </button>
            </div>
            <div style={styles.modalBody}>
              <p style={styles.modalText}>
                Are you sure you want to {bulkActionType} {selectedRegistrations.size} registration{selectedRegistrations.size !== 1 ? 's' : ''}?
              </p>
              <div style={styles.modalDetails}>
                <div><strong>Action:</strong> {bulkActionType === 'convert' ? 'Convert to Fee Event Registration' : 'Overwrite Existing Fee Event Registration'}</div>
                <div><strong>Selected:</strong> {selectedRegistrations.size} registration{selectedRegistrations.size !== 1 ? 's' : ''}</div>
                <div><strong>Filter:</strong> {statusFilter === 'all' ? 'All Registrations' : statusFilter.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
              </div>
              <div style={styles.bulkWarning}>
                <strong>⚠️ Warning:</strong> This action will be applied to all selected registrations. 
                {bulkActionType === 'overwrite' && ' Existing fee event registrations will be updated with survey data.'}
                {bulkActionType === 'convert' && ' New fee event registrations will be created.'}
              </div>
            </div>
            <div style={styles.modalActions}>
              <button
                style={styles.modalCancelButton}
                onClick={() => {
                  setShowBulkConfirmModal(false);
                  setSelectedRegistrations(new Set());
                  setBulkActionType('');
                }}
              >
                Cancel
              </button>
              <button
                style={styles.modalConfirmButton}
                onClick={confirmBulkAction}
                disabled={bulkProcessing}
              >
                {bulkProcessing ? 'Processing...' : `${bulkActionType === 'convert' ? 'Convert' : 'Overwrite'} ${selectedRegistrations.size} Registration${selectedRegistrations.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  title: {
    fontSize: '2rem',
    marginBottom: '0.5rem',
    color: '#333',
    textAlign: 'center'
  },
  subtitle: {
    fontSize: '1rem',
    color: '#666',
    textAlign: 'center',
    marginBottom: '2rem'
  },
  controls: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    alignItems: 'end'
  },
  controlGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    minWidth: '200px'
  },
  label: {
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#333'
  },
  select: {
    padding: '0.5rem',
    fontSize: '0.9rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    backgroundColor: '#fff'
  },
  searchInput: {
    padding: '0.5rem',
    fontSize: '0.9rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    backgroundColor: '#fff'
  },
  stats: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1.5rem',
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    flexWrap: 'wrap'
  },
  statItem: {
    fontSize: '0.9rem',
    color: '#333'
  },
  filterIndicator: {
    fontSize: '0.8rem',
    color: '#1976d2',
    fontWeight: '600',
    fontStyle: 'italic'
  },
  totalCountIndicator: {
    fontSize: '0.8rem',
    color: '#28a745',
    fontWeight: '600',
    fontStyle: 'italic'
  },
  loadingProgress: {
    textAlign: 'center',
    marginTop: '1rem',
    fontSize: '1rem',
    color: '#666',
    fontWeight: '500'
  },
  bulkActions: {
    marginBottom: '1.5rem',
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    border: '1px solid #e0e0e0'
  },
  bulkActionControls: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  bulkActionButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    maxWidth: '200px'
  },
  bulkSelectionControls: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    flexWrap: 'wrap',
    padding: '1rem',
    backgroundColor: '#e3f2fd',
    borderRadius: '4px',
    border: '1px solid #bbdefb'
  },
  bulkSelectButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#1976d2',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  },
  bulkSelectionCount: {
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#1976d2'
  },
  bulkConfirmButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  },
  bulkCancelButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  },
  checkbox: {
    marginRight: '0.5rem',
    transform: 'scale(1.2)',
    cursor: 'pointer'
  },
  bulkModeIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.5rem',
    borderRadius: '4px',
    backgroundColor: '#f8f9fa',
    border: '1px solid #e0e0e0'
  },
  selectedIndicator: {
    color: '#28a745',
    fontWeight: '600',
    fontSize: '0.9rem'
  },
  unselectedIndicator: {
    color: '#6c757d',
    fontSize: '0.9rem'
  },
  bulkWarning: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: '4px',
    padding: '1rem',
    marginTop: '1rem',
    fontSize: '0.9rem',
    color: '#856404'
  },
  registrationsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  registrationCard: {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '1.5rem',
    backgroundColor: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  registrationHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1rem'
  },
  studentInfo: {
    flex: 1
  },
  studentName: {
    fontSize: '1.2rem',
    fontWeight: '600',
    color: '#333',
    margin: '0 0 0.5rem 0'
  },
  studentDetails: {
    display: 'flex',
    gap: '1rem',
    fontSize: '0.9rem',
    color: '#666'
  },
  statusBadges: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap'
  },
  statusBadge: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: '600'
  },
  eventInfo: {
    marginBottom: '1rem',
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px'
  },
  eventTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#333',
    margin: '0 0 0.5rem 0'
  },
  eventDetails: {
    display: 'flex',
    gap: '1rem',
    fontSize: '0.9rem',
    color: '#666',
    flexWrap: 'wrap'
  },
  matchInfo: {
    marginBottom: '1rem',
    padding: '1rem',
    backgroundColor: '#e8f5e8',
    borderRadius: '6px',
    border: '1px solid #c8e6c9'
  },
  matchTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#2e7d32',
    margin: '0 0 0.5rem 0'
  },
  accountInfo: {
    display: 'flex',
    gap: '1rem',
    fontSize: '0.9rem',
    color: '#333',
    flexWrap: 'wrap',
    marginBottom: '0.5rem'
  },
  enrollmentInfo: {
    marginTop: '0.5rem',
    padding: '0.5rem',
    backgroundColor: '#fff',
    borderRadius: '4px'
  },
  enrollmentTitle: {
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#2e7d32',
    margin: '0 0 0.25rem 0'
  },
  enrollmentDetails: {
    display: 'flex',
    gap: '1rem',
    fontSize: '0.8rem',
    color: '#666',
    flexWrap: 'wrap'
  },
  noEnrollmentWarning: {
    fontSize: '0.9rem',
    color: '#ff9800',
    fontWeight: '600',
    marginTop: '0.5rem'
  },
  noMatchInfo: {
    marginBottom: '1rem',
    padding: '1rem',
    backgroundColor: '#ffebee',
    borderRadius: '6px',
    border: '1px solid #ffcdd2'
  },
  noMatchTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#d32f2f',
    margin: '0 0 0.5rem 0'
  },
  noMatchDescription: {
    fontSize: '0.9rem',
    color: '#666',
    margin: 0
  },
  existingRegistrationInfo: {
    marginBottom: '1rem',
    padding: '1rem',
    backgroundColor: '#e3f2fd',
    borderRadius: '6px',
    border: '1px solid #bbdefb'
  },
  existingRegistrationTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#1976d2',
    margin: '0 0 0.5rem 0'
  },
  existingRegistrationDetails: {
    display: 'flex',
    gap: '1rem',
    fontSize: '0.9rem',
    color: '#333',
    flexWrap: 'wrap',
    marginBottom: '0.5rem'
  },
  existingRegistrationNote: {
    fontSize: '0.9rem',
    color: '#1976d2',
    fontWeight: '600',
    fontStyle: 'italic'
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem'
  },
  actionGroup: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center'
  },
  convertButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  },
  overwriteButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#ff9800',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  },
  disabledButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'not-allowed',
    opacity: 0.6
  },
  error: {
    padding: '1rem',
    backgroundColor: '#f8d7da',
    color: '#721c24',
    borderRadius: '4px',
    marginBottom: '1rem',
    border: '1px solid #f5c6cb'
  },
  success: {
    padding: '1rem',
    backgroundColor: '#d4edda',
    color: '#155724',
    borderRadius: '4px',
    marginBottom: '1rem',
    border: '1px solid #c3e6cb'
  },
  noRegistrations: {
    padding: '2rem',
    textAlign: 'center',
    color: '#666',
    fontSize: '1.1rem'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    maxWidth: '500px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 1.5rem',
    borderBottom: '1px solid #e0e0e0'
  },
  modalTitle: {
    margin: 0,
    fontSize: '1.3rem',
    color: '#333'
  },
  modalCloseButton: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
    color: '#666',
    padding: '0.25rem',
    borderRadius: '4px',
    transition: 'background-color 0.3s ease'
  },
  modalBody: {
    padding: '1.5rem'
  },
  modalText: {
    fontSize: '1rem',
    color: '#333',
    marginBottom: '1rem'
  },
  modalDetails: {
    backgroundColor: '#f8f9fa',
    padding: '1rem',
    borderRadius: '4px',
    fontSize: '0.9rem'
  },
  overwriteWarning: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: '4px',
    padding: '1rem',
    marginTop: '1rem',
    fontSize: '0.9rem',
    color: '#856404'
  },
  overwriteList: {
    marginTop: '0.5rem',
    paddingLeft: '1.5rem'
  },
  modalActions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-end',
    padding: '1rem 1.5rem',
    borderTop: '1px solid #e0e0e0'
  },
  modalCancelButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  },
  modalConfirmButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  }
};

export default OpenRegistrationMatcher;

